import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { TenantLink, useTenantSearchParams } from '~/lib/navigation';
import { QueryParams } from '~/lib/queryParams';
import { DotIllustration } from '@butter/ui/icons';
import type { TenantAppConfig } from '@butter/library/shared';
import { AppDownloadModal } from '~/ui/layout/AppDownloadModal';
import { arrivedForTheApp, clubAppLinks } from '~/features/appDownload/clubAppLink';
import { SignInModal } from '~/ui/layout/SignInModal';

/**
 * MobileMenu - Full-screen slide-out menu for mobile devices
 * 
 * Features:
 * - Slides in from right using CSS transforms
 * - Full-screen overlay (covers everything except header)
 * - Accessible keyboard navigation
 * - Focus trap when open
 * - Body scroll lock when open
 * - Integrated modals for sign-in and app download
 * 
 * Design specs:
 * - Background: White with decorative dots pattern
 * - Slides from right to left
 * - Covers entire screen except header
 * - Hamburger icon in header transforms to X
 */

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional tenant store config (iosAppId / androidPackageName / handle). */
  tenantConfig?: TenantAppConfig;
  /** Drives which auth action the menu offers. Passed down from MainLayout. */
  isAuthenticated: boolean;
  /** Shows "Book a Lesson" — the club has a bookable instructor. @default false */
  showBookLesson?: boolean;
  /** Shows "Lesson Packages" — the club lists a lesson package. @default false */
  showLessonPackages?: boolean;
  /** Member holds a lesson pack at this club — shows "Plans". @default false */
  showPlans?: boolean;
  /** Sign the member out. Owned by MainLayout, same handler the header uses. */
  onLogout: () => void;
}

export function MobileMenu({
  isOpen,
  onClose,
  tenantConfig,
  isAuthenticated,
  showBookLesson = false,
  showLessonPackages = false,
  showPlans = false,
  onLogout,
}: MobileMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  // A club link lands with `get-app=1`: open the download sheet once, on load.
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(arrivedForTheApp);
  // …then drop `get-app`, so a refresh or a shared URL does not reopen it.
  const [searchParams, setSearchParams] = useTenantSearchParams();
  useEffect(() => {
    if (searchParams.has(QueryParams.GET_APP)) setSearchParams({ [QueryParams.GET_APP]: null });
  }, [searchParams, setSearchParams]);
  const { pathname } = useLocation();
  const isCurrent = (to: string) =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
  const isBookLessonCurrent =
    pathname.startsWith('/lessons/book') || pathname.startsWith('/lessons/confirmed');
  const isLessonPackagesCurrent =
    pathname === '/lessons' || pathname.startsWith('/lessons/purchase');

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Keyboard handler for Escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);

      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  // Handlers for modal actions
  const handleSignInClick = () => {
    setShowSignInModal(true);
  };

  const handleAppDownloadClick = () => {
    setShowAppDownloadModal(true);
  };

  const handleSignInSuccess = () => {
    setShowSignInModal(false);
    onClose(); // Close mobile menu after successful sign-in
  };

  // Close first, then sign out: logging out re-renders the tree, and a menu left
  // open over the signed-out view has nothing meaningful in it.
  const handleSignOutClick = () => {
    onClose();
    onLogout();
  };

  return (
    <>
      {/* Backdrop - subtle overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/10 z-40 md:hidden transition-opacity duration-300"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Menu Panel */}
      <div
        ref={menuRef}
        className={`
          fixed top-16 right-0 bottom-0 w-full bg-white z-50 md:hidden
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile menu"
      >
        {/* Decorative background dots pattern */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute left-0 top-[40%] -translate-y-1/2 w-[52px] h-full max-h-[812px]">
            <DotIllustration className="w-full h-full" />
          </div>

          {/* Gradient blob decoration (top right) */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-theme-100 to-transparent rounded-full blur-3xl opacity-30 transform translate-x-1/2 -translate-y-1/2" />
        </div>

        {/* Menu Content */}
        <div className="relative h-full flex flex-col px-6 py-8">
          {/* Spacer to push content to bottom */}
          <div className="flex-1" />

          {/* Menu Title */}
          <h2 className="text-3xl font-semibold text-gray-900 mb-8">
            Menu
          </h2>

          {/* Navigation Links */}
          <nav className="space-y-4 mb-8" aria-label="Mobile navigation">
            <TenantLink
              to="/"
              onClick={() => {
                window.dispatchEvent(new Event('butter:clearBooking'));
                onClose();
              }}
              aria-current={isCurrent('/') ? 'page' : undefined}
              className="
                block w-full py-3 px-6
                text-base font-medium text-gray-900
                hover:bg-gray-50 rounded-lg
                transition-colors
                focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2
                aria-[current=page]:text-theme-800
              "
            >
              Make a Booking
            </TenantLink>
            <TenantLink
              to="/subscriptions"
              onClick={onClose}
              aria-current={isCurrent('/subscriptions') ? 'page' : undefined}
              className="
                block w-full py-3 px-6
                text-base font-medium text-gray-900
                hover:bg-gray-50 rounded-lg
                transition-colors
                focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2
                aria-[current=page]:text-theme-800
              "
            >
              Subscriptions
            </TenantLink>
            {showBookLesson && (
              <TenantLink
                to="/lessons/book"
                onClick={onClose}
                aria-current={isBookLessonCurrent ? 'page' : undefined}
                data-testid="mobile-menu-book-lesson-link"
                className="
                  block w-full py-3 px-6
                  text-base font-medium text-gray-900
                  hover:bg-gray-50 rounded-lg
                  transition-colors
                  focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2
                  aria-[current=page]:text-theme-800
                "
              >
                Book a Lesson
              </TenantLink>
            )}
            {showLessonPackages && (
              <TenantLink
                to="/lessons"
                onClick={onClose}
                aria-current={isLessonPackagesCurrent ? 'page' : undefined}
                data-testid="mobile-menu-lessons-link"
                className="
                  block w-full py-3 px-6
                  text-base font-medium text-gray-900
                  hover:bg-gray-50 rounded-lg
                  transition-colors
                  focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2
                  aria-[current=page]:text-theme-800
                "
              >
                Lesson Packages
              </TenantLink>
            )}
            {showPlans && (
              <TenantLink
                to="/plans"
                onClick={onClose}
                aria-current={isCurrent('/plans') ? 'page' : undefined}
                data-testid="mobile-menu-plans-link"
                className="
                  block w-full py-3 px-6
                  text-base font-medium text-gray-900
                  hover:bg-gray-50 rounded-lg
                  transition-colors
                  focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2
                  aria-[current=page]:text-theme-800
                "
              >
                Plans
              </TenantLink>
            )}
          </nav>

          {/* Action Buttons */}
          <div className="space-y-3 pb-8">
            {/* Get the App Button - Primary */}
            <button
              onClick={handleAppDownloadClick}
              className="
                block w-full py-4 px-6 
                text-base font-semibold text-center text-white rounded-full
                bg-theme-600 hover:bg-theme-700
                transition-colors
                focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2
              "
            >
              Get the App
            </button>

            {/* Auth Button - Secondary. The header hides its sign-in/sign-out pair
                below md, so this is the only auth affordance on a phone. */}
            {isAuthenticated ? (
              <button
                onClick={handleSignOutClick}
                data-testid="mobile-menu-signout-btn"
                className="
                  block w-full py-4 px-6
                  text-base font-semibold text-center text-fg-quaternary rounded-full
                  bg-white border-2 border-theme-600
                  hover:bg-gray-50
                  transition-colors
                  focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2
                "
              >
                Sign out
              </button>
            ) : (
              <button
                onClick={handleSignInClick}
                data-testid="mobile-menu-signin-btn"
                className="
                  block w-full py-4 px-6
                  text-base font-semibold text-center text-fg-quaternary rounded-full
                  bg-white border-2 border-theme-600
                  hover:bg-gray-50
                  transition-colors
                  focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2
                "
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <SignInModal
        isOpen={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        onSuccess={handleSignInSuccess}
      />
      <AppDownloadModal
        isOpen={showAppDownloadModal}
        onClose={() => setShowAppDownloadModal(false)}
        tenantConfig={tenantConfig}
        {...clubAppLinks(tenantConfig)}
      />
    </>
  );
}
